/**
 * Що потребує уваги на дашборді.
 *
 * Рахується з даних, які `/dashboard/summary/` вже віддає — окремих запитів
 * немає. Зараз два джерела: прострочені сушіння і дефіцит чорнових заготовок.
 */
import { formatMoney } from '../../utils/formatters';
import type {
  ChamberStatus,
  DeficitRow,
  FinanceSummary,
  VersionWithoutNorm,
} from '../../types/dashboard';

export interface AttentionItem {
  key: string;
  name: string;
  sub: string;
  value: string;
  path: string;
  tone: 'error' | 'warning';
}

const DAY_MONTH: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit' };

/** Камери, де фактичний день сушіння перевищив прогнозний. */
export function overdueChambers(chambers: ChamberStatus[]): ChamberStatus[] {
  return chambers.filter(
    (c) => c.days_total !== null && c.days_elapsed !== null && c.days_elapsed > c.days_total,
  );
}

function daysWord(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return 'день';
  return 'днів';
}

export function buildAttention(
  chambers: ChamberStatus[],
  deficit: DeficitRow[],
  finance: FinanceSummary,
  versionsWithoutNorm: VersionWithoutNorm[] = [],
): AttentionItem[] {
  // Порядок: спершу термінове (червоне), потім грошове (жовте).
  const items: AttentionItem[] = [];

  for (const c of overdueChambers(chambers)) {
    const over = c.days_elapsed! - c.days_total!;
    const plan = c.forecast_end_date
      ? ` — план до ${new Date(c.forecast_end_date).toLocaleDateString('uk-UA', DAY_MONTH)}`
      : '';
    items.push({
      key: `chamber-${c.id}`,
      name: `${c.process_code ?? 'Сушіння'} · камера №${c.number}`,
      sub: `сушіння прострочено${plan}`,
      value: `+${over} ${daysWord(over)}`,
      path: '/drying',
      tone: 'error',
    });
  }

  for (const d of deficit) {
    items.push({
      key: `deficit-${d.blank_id}-${d.grade}`,
      name: `${d.blank_name}${d.grade_display ? ` · ${d.grade_display}` : ''}`,
      sub: `потреба ${d.demand_m3} м³ · наявно ${d.available_m3} м³`,
      value: `−${d.deficit_m3} м³`,
      path: '/warehouses/blank-demand',
      tone: 'error',
    });
  }

  // Версія завелася сама, коли менеджер обрав у замовленні комбінацію, якої
  // ще не було. Доти, доки норми немає, заготівельний цех списує ЧЗ за
  // основною версією — тобто приблизно.
  for (const v of versionsWithoutNorm) {
    items.push({
      key: `no-norm-${v.product_id}-${v.variant_label}`,
      name: `${v.product_article || v.product_name} · ${v.variant_label}`,
      sub: `норму не задано — ЧЗ списуються за основною версією · ${v.orders.join(', ')}`,
      value: 'без норми',
      path: '/references/products',
      tone: 'warning',
    });
  }

  // Борг рахується за ВЕСЬ час, а не за місяць (apps/dashboard/views.py:747).
  const owed = parseFloat(finance.this_month.owed_uah);
  if (owed > 0) {
    items.push({
      key: 'owed',
      name: 'Не оплачено за отриману деревину',
      sub: 'деревину отримано, оплату не внесено — за весь час',
      value: formatMoney(owed),
      path: '/purchases/raw',
      tone: 'warning',
    });
  }

  return items;
}
